import React, { useState, useEffect, useRef } from 'react';
import TodoRegisterLayer from '../../../components/todo/TodoRegisterLayer';
import './WorkOrderChecklistTab.css';

function WorkOrderChecklistTab({
  checklists = [],
  newChecklist = '',
  setNewChecklist,
  handleAddChecklist,
  handleToggleChecklist,
  handleEditChecklist,
  handleDeleteChecklist,
  currentUser,
  isAuthor,
  isAssignee,
  isCc,
  workOrder,
  focusId
}) {
  const [viewMode, setViewMode] = useState(isCc ? 'ALL' : 'MINE'); // 기본: 내 체크리스트 (참조자는 전체보기)
  const [hideCompleted, setHideCompleted] = useState(false);

  // 알림/To-Do 클릭 시 포커스 스크롤 및 3초 하이라이트 상태
  const checklistScrollContainerRef = useRef(null);
  const [highlightChecklistId, setHighlightChecklistId] = useState(null);
  const focusHandledRef = useRef(null);

  useEffect(() => {
    if (focusId && checklists && checklists.length > 0 && focusHandledRef.current !== String(focusId)) {
      let attempts = 0;
      const tryScroll = setInterval(() => {
        attempts++;
        const targetEl = document.getElementById(`checklist-${focusId}`);
        const container = checklistScrollContainerRef.current;

        if (targetEl && container && container.clientHeight > 0) {
          clearInterval(tryScroll);
          requestAnimationFrame(() => {
            const contRect = container.getBoundingClientRect();
            const targetRect = targetEl.getBoundingClientRect();
            container.scrollTop = container.scrollTop + (targetRect.top - contRect.top) - (contRect.height / 2) + (targetRect.height / 2);
            focusHandledRef.current = String(focusId);
            setHighlightChecklistId(String(focusId));
            setTimeout(() => {
              setHighlightChecklistId(null);
            }, 3100);
          });
        }
        if (attempts >= 30) clearInterval(tryScroll);
      }, 100);

      return () => clearInterval(tryScroll);
    }
  }, [focusId, checklists]);

  // 텍스트 인라인 에디팅 상태
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');

  // 빠른 추가 및 수정 Textarea 참조
  const quickAddRef = useRef(null);
  const editAreaRef = useRef(null);

  // 작성자별 아코디언 상태
  const [expandedGroups, setExpandedGroups] = useState({});

  // To-Do 등록 팝업 상태
  const [todoAnchorEl, setTodoAnchorEl] = useState(null);
  const [todoSource, setTodoSource] = useState(null);

  // 긴 내용 더보기/접기 상태
  const [expandedItems, setExpandedItems] = useState({});

  const currentUserIdStr = String(currentUser?.userid || currentUser?.id || '');
  const isLocked = ['stop', 'done', 'completed', 'cancel', 'canceled'].includes(workOrder?.status);

  const isContentLong = (content) => {
    if (!content) return false;
    return content.split('\n').length > 3 || content.length > 100;
  };

  // 탭 변경 시 화면 UI 초기화 로직
  const handleTabChange = (mode) => {
    if (viewMode === mode) return;
    setViewMode(mode);
    setEditingId(null);
    setEditContent('');
    setExpandedItems({});
    setHideCompleted(false);
    if (setNewChecklist) setNewChecklist('');
  };

  // 작성자별 그룹 초기 펼침 세팅
  useEffect(() => {
    if (viewMode === 'GROUP') {
      const gData = checklists.reduce((acc, curr) => {
        const uId = curr.user?.userid || curr.user?.id || curr.user_id || 'unknown';
        if (!acc[uId]) acc[uId] = { user: curr.user || { name: '작성자', userid: uId } };
        return acc;
      }, {});

      const sortedGroups = Object.values(gData).sort((a, b) => {
        const aId = String(a.user?.userid || a.user?.id || '');
        const bId = String(b.user?.userid || b.user?.id || '');
        if (aId === currentUserIdStr) return -1;
        if (bId === currentUserIdStr) return 1;
        return (a.user?.name || '').localeCompare(b.user?.name || '');
      });

      const groups = {};
      if (sortedGroups.length > 0) {
        const firstId = sortedGroups[0].user?.userid || sortedGroups[0].user?.id;
        if (firstId) groups[firstId] = true;
      }
      setExpandedGroups(groups);
    }
  }, [viewMode, checklists, currentUserIdStr]);

  // Textarea 자동 높이 조절 로직
  const handleTextareaResize = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  // 날짜 포맷 (YYYY-MM-DD HH:mm:ss)
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const pad = n => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // To-Do 등록 레이어 오픈
  const handleOpenTodoRegister = (e, item) => {
    e.stopPropagation();
    setTodoAnchorEl({ current: e.currentTarget });
    setTodoSource({
      ref_type: 'work_order_checklist',
      ref_table: 'tdx_work_order_checklists',
      ref_id: item.id,
      add_title: workOrder?.title || '업무협조',
      add_subtype: 'checklist',
      add_id1: workOrder?.id,
      add_id2: item.id,
      title: `[업무협조 체크리스트] ${workOrder?.title || ''}`,
      memo: item.content || ''
    });
  };

  // 항목 토글 (완료된 업무에서도 기존 항목의 완료 체크/해제는 가능하도록 허용)
  const handleToggle = (id, currentStatus) => {
    if (handleToggleChecklist) handleToggleChecklist(id, currentStatus);
  };

  // 항목 삭제
  const handleDelete = (id) => {
    if (handleDeleteChecklist) handleDeleteChecklist(id);
  };

  // 퀵 추가 등록
  const handleQuickAddSave = (e) => {
    if (handleAddChecklist) {
      handleAddChecklist(e);
      if (quickAddRef.current) {
        quickAddRef.current.style.height = 'auto';
        quickAddRef.current.focus();
      }
    }
  };

  // 수정 시작
  const startEdit = (item) => {
    setEditingId(item.id);
    setEditContent(item.content);
    setTimeout(() => {
      if (editAreaRef.current) {
        editAreaRef.current.style.height = 'auto';
        editAreaRef.current.style.height = `${editAreaRef.current.scrollHeight}px`;
        editAreaRef.current.focus();
      }
    }, 10);
  };

  // 수정 저장
  const saveEdit = () => {
    if (editContent.trim() !== '') {
      if (handleEditChecklist) handleEditChecklist(editingId, editContent.trim());
    }
    setEditingId(null);
  };

  const toggleGroup = (userId) => {
    setExpandedGroups(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  // 권한 판별: 지시자이거나 본인이 작성한 항목 (비활성 상태이거나 참조자일 때는 수정/삭제 불가)
  const canEditOrDelete = (item) => {
    if (isLocked || isCc) return false;
    const itemUserId = String(item.user?.userid || item.user?.id || item.user_id || '');
    return isAuthor || itemUserId === currentUserIdStr;
  };

  // --- [데이터 집계 및 정렬] ---
  const baseItems = viewMode === 'MINE'
    ? checklists.filter(c => {
        const itemUserId = String(c.user?.userid || c.user?.id || c.user_id || '');
        return itemUserId === currentUserIdStr;
      })
    : checklists;

  const getFilteredData = () => {
    let filtered = [...baseItems];
    if (hideCompleted) filtered = filtered.filter(c => !c.is_completed);

    filtered.sort((a, b) => {
      if (a.is_completed !== b.is_completed) {
        return a.is_completed ? 1 : -1;
      }
      return new Date(b.created_at) - new Date(a.created_at);
    });

    return filtered;
  };

  const filteredData = getFilteredData();
  const overallProgress = baseItems.length === 0 ? 0 : Math.round((baseItems.filter(c => c.is_completed).length / baseItems.length) * 100);

  const groupedData = filteredData.reduce((acc, curr) => {
    const uId = curr.user?.userid || curr.user?.id || curr.user_id || 'unknown';
    if (!acc[uId]) acc[uId] = { user: curr.user || { name: '작성자', userid: uId }, items: [] };
    acc[uId].items.push(curr);
    return acc;
  }, {});

  // --- [원형 진척도 차트 (SVG Ring)] ---
  const radius = 12;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (overallProgress / 100) * circumference;

  // --- [렌더링 헬퍼: 프리미엄 리스트 아이템] ---
  const renderItem = (item) => {
    const isEditing = editingId === item.id;
    const itemAuthorName = item.user?.name || item.author_name || '작성자';
    const itemAuthorPos = item.user?.org?.position || item.user?.position || '';

    return (
      <div
        id={`checklist-${item.id}`}
        key={item.id}
        className={`wo-checklist-item-container d-flex align-items-start px-3 py-2 border-bottom ${item.is_completed ? 'wo-checklist-completed-item' : 'bg-white'} ${highlightChecklistId === String(item.id) ? 'highlight-comment' : ''}`}
      >
        {/* 둥근 체크박스 (참조자는 토글 차단) */}
        <div
          className={`me-3 mt-1 flex-shrink-0 ${isCc ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
          onClick={() => {
            if (isCc) return;
            handleToggle(item.id, item.is_completed);
          }}
          title={isCc ? '참조자는 체크리스트를 열람만 할 수 있습니다.' : undefined}
        >
          <div className={`wo-premium-checkbox ${item.is_completed ? 'checked' : ''}`}>
            {item.is_completed && <i className="fas fa-check text-white" style={{ fontSize: '8px' }} />}
          </div>
        </div>

        {/* 내용 영역 */}
        <div className="flex-grow-1 min-w-0 position-relative" style={{ paddingTop: '2px' }}>
          {isEditing ? (
            // [수정 모드]
            <div className="bg-white rounded-3 border border-primary shadow-sm d-flex flex-column overflow-hidden">
              <textarea
                ref={editAreaRef}
                className="form-control border-0 shadow-none p-2 wo-checklist-custom-textarea wo-checklist-custom-scrollbar bg-transparent flex-grow-1"
                style={{ fontSize: '12px', lineHeight: '1.5', resize: 'none', minHeight: '36px' }}
                value={editContent}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setEditContent(e.target.value);
                    handleTextareaResize(e);
                  }
                }}
                maxLength={500}
              />
              <div className="d-flex justify-content-between align-items-center bg-light px-2 py-1" style={{ borderTop: '1px solid #e9ecef' }}>
                <div className="text-muted fw-bold ps-1" style={{ fontSize: '0.65rem' }}>
                  {editContent.length}/500
                </div>
                <div className="d-flex gap-1">
                  <button
                    type="button"
                    className="btn btn-sm bg-white border shadow-sm px-2"
                    style={{ fontSize: '0.72rem', borderRadius: '4px', paddingTop: '2px', paddingBottom: '2px' }}
                    onClick={() => setEditingId(null)}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary shadow-sm fw-bold px-2"
                    style={{ fontSize: '0.72rem', borderRadius: '4px', paddingTop: '2px', paddingBottom: '2px' }}
                    onClick={saveEdit}
                  >
                    저장
                  </button>
                </div>
              </div>
            </div>
          ) : (
            // [읽기 모드]
            <div className="d-flex flex-column w-100 position-relative">
              {/* 본문 텍스트 (Line Clamp 적용) */}
              <div
                className={`pe-5 ${item.is_completed ? 'text-decoration-line-through opacity-50' : 'text-dark'}`}
                style={{
                  fontSize: '12.5px',
                  fontWeight: '400',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  display: expandedItems[item.id] || !isContentLong(item.content) ? 'block' : '-webkit-box',
                  WebkitLineClamp: expandedItems[item.id] || !isContentLong(item.content) ? 'unset' : 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: expandedItems[item.id] || !isContentLong(item.content) ? 'visible' : 'hidden'
                }}
              >
                {item.content}
              </div>

              {/* 하단 제어부: 더보기/접기 + 메타데이터 */}
              <div className="d-flex align-items-center mt-1 w-100">
                {isContentLong(item.content) && (
                  <span
                    className="text-primary fw-bold"
                    style={{ fontSize: '11px', cursor: 'pointer' }}
                    onClick={() => setExpandedItems(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                  >
                    {expandedItems[item.id] ? '- 접기' : '+ 더보기'}
                  </span>
                )}
                <div className="d-flex align-items-center gap-2 text-muted wo-checklist-meta-info ms-auto" style={{ fontSize: '11px' }}>
                  {viewMode !== 'MINE' && (
                    <span className="fw-medium text-secondary">{itemAuthorName} {itemAuthorPos}</span>
                  )}
                  <span style={{ fontSize: '10px' }}>
                    <i className="far fa-clock me-1 opacity-50" />
                    {formatDateTime(item.created_at)}
                  </span>
                </div>
              </div>

              {/* 호버 액션 박스 */}
              <div className="wo-checklist-hover-actions d-flex gap-1 align-items-center bg-white rounded-2 shadow-sm px-1 py-1 border position-absolute top-0 end-0">
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="btn btn-link p-1 text-secondary hover-info border-0 shadow-none"
                    onClick={(e) => handleOpenTodoRegister(e, item)}
                    title="To-Do 등록"
                  >
                    <i className="fas fa-tasks" style={{ fontSize: '0.75rem' }} />
                  </button>
                  {todoSource && todoSource.ref_id === item.id && (
                    <TodoRegisterLayer
                      source={todoSource}
                      anchorRef={todoAnchorEl}
                      onClose={() => {
                        setTodoSource(null);
                        setTodoAnchorEl(null);
                      }}
                      direction="down"
                      align="right"
                    />
                  )}
                </div>
                {canEditOrDelete(item) && (
                  <>
                    <button
                      type="button"
                      className="btn btn-link p-1 text-secondary hover-primary border-0 shadow-none"
                      onClick={(e) => { e.stopPropagation(); startEdit(item); }}
                      title="수정"
                    >
                      <i className="fas fa-pencil-alt" style={{ fontSize: '0.75rem' }} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-link p-1 text-secondary hover-danger border-0 shadow-none"
                      onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                      title="삭제"
                    >
                      <i className="far fa-trash-alt" style={{ fontSize: '0.75rem' }} />
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="d-flex flex-column h-100 p-1" style={{ fontFamily: "'Inter', 'Pretendard', sans-serif" }}>
      {/* 메인 컨테이너 */}
      <div className="bg-white rounded-3 border shadow-sm d-flex flex-column flex-grow-1" style={{ borderColor: '#dee2e6', overflow: 'hidden', minHeight: 0 }}>
        {/* 1. 최상단 통합 헤더 */}
        <div className="px-3 pt-2 border-bottom bg-light d-flex justify-content-between align-items-end flex-shrink-0">
          <div className="wo-checklist-modern-tabs">
            <div className={`wo-checklist-tab-btn ${viewMode === 'MINE' ? 'active' : ''}`} onClick={() => handleTabChange('MINE')}>
              내 체크리스트
            </div>
            <div className={`wo-checklist-tab-btn ${viewMode === 'ALL' ? 'active' : ''}`} onClick={() => handleTabChange('ALL')}>
              전체 보기
            </div>
            <div className={`wo-checklist-tab-btn ${viewMode === 'GROUP' ? 'active' : ''}`} onClick={() => handleTabChange('GROUP')}>
              작성자별
            </div>
          </div>

          <div className="d-flex align-items-center gap-3 pb-2">
            {/* 완료 숨기기 토글 */}
            <div className="form-check form-switch mb-0 p-0 d-flex align-items-center" style={{ minHeight: 'auto' }}>
              <input
                className="form-check-input cursor-pointer shadow-sm m-0 float-none"
                type="checkbox"
                id="woHideCompleteToggle"
                checked={hideCompleted}
                onChange={(e) => setHideCompleted(e.target.checked)}
                style={{ width: '30px', height: '16px' }}
              />
              <label className="form-check-label ms-2 text-muted cursor-pointer fw-bold mb-0" htmlFor="woHideCompleteToggle" style={{ fontSize: '0.72rem', lineHeight: '16px' }}>
                완료 숨기기
              </label>
            </div>

            {/* 통합 통계 위젯 (총 갯수 + 진척도) */}
            <div className="d-flex align-items-center gap-2 bg-white border rounded-pill px-3 py-1 shadow-sm">
              <span className="fw-bold text-muted" style={{ fontSize: '12px' }}>
                총 <span className="text-dark fw-bolder">{baseItems.length}</span>건
              </span>

              <div className="border-start border-2 mx-1" style={{ height: '14px' }} />

              <div className="d-flex align-items-center gap-1" title={`진척도: ${overallProgress}%`}>
                <svg width="24" height="24" viewBox="0 0 28 28">
                  <circle cx="14" cy="14" r={radius} fill="none" stroke="#e9ecef" strokeWidth="3" />
                  <circle
                    cx="14"
                    cy="14"
                    r={radius}
                    fill="none"
                    stroke="#4c6ef5"
                    strokeWidth="3"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 0.5s ease', transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
                  />
                </svg>
                <span className="fw-bolder text-dark" style={{ fontSize: '0.75rem' }}>{overallProgress}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* 2. 리스트 메인 영역 */}
        <div ref={checklistScrollContainerRef} className="flex-grow-1 overflow-auto wo-checklist-custom-scrollbar bg-white">
          {checklists.length === 0 ? (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted py-5">
              <i className="fas fa-clipboard-check fs-1 mb-3 opacity-25" />
              <h6 className="fw-bold fs-7">등록된 체크리스트가 없습니다.</h6>
            </div>
          ) : (
            <div>
              {viewMode === 'GROUP' ? (
                // [작성자별 그룹 뷰]
                Object.values(groupedData).sort((a, b) => {
                  const aId = String(a.user?.userid || a.user?.id || '');
                  const bId = String(b.user?.userid || b.user?.id || '');
                  if (aId === currentUserIdStr) return -1;
                  if (bId === currentUserIdStr) return 1;
                  return (a.user?.name || '').localeCompare(b.user?.name || '');
                }).map((group, index) => {
                  const uId = group.user?.userid || group.user?.id || index;
                  const isExpanded = expandedGroups[uId];
                  const groupTotal = group.items.length;
                  const groupCompleted = group.items.filter(i => i.is_completed).length;

                  return (
                    <div key={uId}>
                      <div
                        className="d-flex align-items-center justify-content-between px-3 py-2 bg-light"
                        style={{ borderTop: index === 0 ? '1px solid #ced4da' : '1px solid #e9ecef', borderBottom: '1px solid #dee2e6', cursor: 'pointer' }}
                        onClick={() => toggleGroup(uId)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div className="d-flex align-items-center justify-content-center cursor-pointer" style={{ width: '20px', height: '20px' }}>
                            <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-secondary`} style={{ fontSize: '13px' }} />
                          </div>
                          <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>
                            {group.user?.name || '작성자'} {group.user?.org?.position || group.user?.position || ''}
                          </span>
                        </div>
                        <span className="fw-bold text-muted bg-white border px-2 py-0 rounded-pill" style={{ fontSize: '12px' }}>
                          {groupCompleted} / {groupTotal}
                        </span>
                      </div>

                      {isExpanded && (
                        <div className="bg-white">
                          {group.items.map(renderItem)}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                // [전체/내할일 플랫 뷰]
                <div>
                  {filteredData.map(renderItem)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 3. 고정된 하단 퀵 추가 입력창 */}
        {isLocked ? (
          <div className="wo-checklist-sticky-quick-add px-3 py-3 border-top flex-shrink-0 bg-light text-center text-muted" style={{ fontSize: '12.5px' }}>
            <i className="fas fa-lock me-1.5 text-secondary" /> 진행 중인 업무만 체크리스트를 추가할 수 있습니다.
          </div>
        ) : isCc ? (
          <div className="wo-checklist-sticky-quick-add px-3 py-3 border-top flex-shrink-0 bg-light text-center text-muted" style={{ fontSize: '12.5px' }}>
            <i className="fas fa-lock me-1.5 text-secondary" /> 참조자는 체크리스트를 열람(조회)만 할 수 있습니다.
          </div>
        ) : (viewMode === 'ALL' || viewMode === 'MINE') && (
          <div className="wo-checklist-sticky-quick-add px-3 py-3 border-top flex-shrink-0">
            <div className="wo-checklist-quick-add-input-wrapper bg-white border border-light-subtle rounded-3 shadow-sm d-flex flex-column overflow-hidden">
              <div className="d-flex align-items-start p-2">
                <i className="fas fa-plus text-primary opacity-50 me-2 mt-1 fs-6 flex-shrink-0" />
                <textarea
                  ref={quickAddRef}
                  className="form-control border-0 shadow-none p-0 wo-checklist-custom-scrollbar bg-transparent flex-grow-1"
                  style={{ fontSize: '12.5px', resize: 'none', minHeight: '36px', maxHeight: '120px', lineHeight: '1.5' }}
                  placeholder="새로운 체크리스트를 입력하세요. (Enter 또는 등록 버튼)"
                  value={newChecklist || ''}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setNewChecklist(e.target.value);
                      handleTextareaResize(e);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleQuickAddSave(e);
                    }
                  }}
                />
              </div>
              <div className="d-flex justify-content-between align-items-center bg-light px-2 py-1" style={{ borderTop: '1px solid #e9ecef' }}>
                <div className="text-muted fw-bold ps-1" style={{ fontSize: '0.65rem' }}>
                  {(newChecklist || '').length}/500
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-primary px-3 shadow-sm fw-bold d-flex align-items-center gap-1"
                  style={{ fontSize: '0.72rem', borderRadius: '4px', paddingTop: '3px', paddingBottom: '3px' }}
                  onClick={handleQuickAddSave}
                  disabled={!newChecklist || newChecklist.trim() === ''}
                >
                  <i className="fas fa-paper-plane" /> 등록
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkOrderChecklistTab;
